import React from "react"

import { Helmet } from "react-helmet"
import { useIntl } from "react-intl"

import useCategories from "~/hooks/useCategories"
import useSiteMetadata from "~/hooks/useSiteMetadata"
import { RecipeAlgoliaHit } from "~/models/Recipe"
import { ResultState } from "~/templates/Category/types"
import { getCategoryById } from "~/utils/createPages/categoriesUtils"

type PropTypes = {
  baseResultState: ResultState<RecipeAlgoliaHit>
  listingTitle: string
  listingSEODescription?: string
}

const FilteredPagesJsonLd = ({
  baseResultState,
  listingTitle,
  listingSEODescription,
}: PropTypes) => {
  const { siteUrl } = useSiteMetadata()
  const intl = useIntl()

  const CATEGORIES = useCategories(intl.locale)

  const firstHit = baseResultState?.hits && baseResultState?.hits[0]

  const categoryId = firstHit && firstHit.categories[0]
  const category = categoryId ? getCategoryById(categoryId, CATEGORIES) : null

  const jsonld = `{
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [${baseResultState?.hits?.map(
      (hit: RecipeAlgoliaHit, index) => `{
          "@type": "ListItem",
          "position": ${index + 1},
          "image": "${hit.web_cover_thumbnail}",
          "url": "${siteUrl + hit.absolute_url}",
          "name": "${hit.title}"
        }`
    )}
     ]
  }`

  const recipeMarkup = firstHit
    ? `{
       "@context": "https://schema.org/",
       "@type": "Recipe",
       "name": "${listingTitle}",
       "image": [
         "${firstHit.web_cover}",
       ],
       "author": {
         "@type": "Organization",
         "name": "Chefclub Network"
       },
       "datePublished": "${firstHit.published_date}",
       "description": "${
         listingSEODescription ??
         intl.formatMessage({
           id: "category/text:default-description",
         })
       }",
       "video": {
         "@type": "VideoObject",
         "name": "${category?.title ?? ""}",
         "description": "${firstHit.title}",
         "thumbnailUrl": "${firstHit.web_cover_thumbnail}",
         "contentUrl": "https://medias.chefclub.tools/${firstHit.id}.mp4",
         "embedUrl": "${siteUrl + firstHit.absolute_url}",
         "uploadDate": "${firstHit.created_time}"
       }
    }`
    : "{}"

  const ldJsonRating = firstHit?.ratings_count
    ? {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: `${firstHit?.ratings_average?.toFixed(1)}`,
          ratingCount: `${firstHit?.ratings_count}`,
        },
      }
    : {}

  let parseRecipeMarkup
  try {
    parseRecipeMarkup = firstHit && recipeMarkup ? JSON.parse(recipeMarkup) : {}
  } catch (e) {
    parseRecipeMarkup = {}
  }

  const listingMarkup = {
    ...parseRecipeMarkup,
    ...(firstHit?.ratings_count && ldJsonRating),
  }

  return (
    <Helmet>
      <script type="application/ld+json">{jsonld}</script>
      {firstHit && (
        <script type="application/ld+json">
          {JSON.stringify(listingMarkup)}
        </script>
      )}
    </Helmet>
  )
}

export default FilteredPagesJsonLd
